import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useNavigation} from '@react-navigation/native';
import {store, useAppDispatch} from '../../../redux';
import {enterCardActivationSession} from '../../../redux/actions/cardActivation';
import {loginSlice} from '../../../redux/reducers/login';
import {loginStepSlice} from '../../../redux/reducers/runtime/loginStep';
import {LoginState} from '../../../shared/models/login/loginStep';
import {AuthorizeEsiaResponsePayload} from '../../../shared/models/api/authorizeEsiaResponsePayload';
import {GetHolderProfileCabinetResponsePayload} from '../../../shared/models/api';
import {Screens} from '../../../shared/routing';
import {useModal} from '../../../shared/components/modal';
import {EsiaRegCardFlowHandler} from '../../login/esiaRegCard/components/form/desktop';
import {useRegCardState} from '../../login/esiaRegCard/esiaRegCardState/useRegCardState';
import {EsiaRegStep} from '../../profile/desktop/components/applications/view';
import {useReissueContinue} from './useReissueContinue';
import {useReissueEsia} from './useReissueEsia';
import {useReissueReturnFromEsia} from './useReissueReturnFromEsia';
import {subscribeEsiaLogoutForCardReissue} from '../../../shared/components/esiaLogoutWebView';

type Params = {
  isFocused: boolean;
  isDesktop: boolean;
  isAuthorized: boolean;
  esiaRegInfo: AuthorizeEsiaResponsePayload | null;
  holderProfile: GetHolderProfileCabinetResponsePayload | null;
  hideAndLockGlobalLoader: () => void;
  cancelEsia: () => Promise<void>;
  unlockGlobalLoader: () => void;
  showGlobalLoader: () => void;
  setIsFormModalOpen: (open: boolean) => void;
};

export type CardReissueScreenFlow = {
  submitting: boolean;
  esiaLogoutLink: string | null;
  onEsiaClick: () => Promise<void>;
  onContinue: () => Promise<void>;
};

export const useCardReissueScreenFlow = ({
  isFocused,
  isDesktop,
  isAuthorized,
  esiaRegInfo,
  holderProfile,
  hideAndLockGlobalLoader,
  cancelEsia,
  unlockGlobalLoader,
  showGlobalLoader,
  setIsFormModalOpen,
}: Params): CardReissueScreenFlow => {
  const dispatch = useAppDispatch();
  const navigation = useNavigation();
  const modals = useModal();
  const {addData, setParent} = useRegCardState();
  const [esiaLogoutLink, setEsiaLogoutLink] = useState<string | null>(null);
  const handledEsiaTokenRef = useRef<string | null>(null);
  const activationOpenedRef = useRef(false);
  const useDesktopReissueForm = isDesktop;

  useEffect(() => subscribeEsiaLogoutForCardReissue(setEsiaLogoutLink), []);

  const openActivation = useCallback(() => {
    hideAndLockGlobalLoader();
    void dispatch(enterCardActivationSession());
  }, [hideAndLockGlobalLoader, dispatch]);

  const prepareRegCardState = useCallback(
    (regInfo: AuthorizeEsiaResponsePayload) => {
      addData(regInfo);
      setParent({...regInfo, checked: true});
    },
    [addData, setParent],
  );

  const closeFormModal = useCallback(() => {
    if (store.getState().login.esiaUserHasNoCards) {
      setIsFormModalOpen(false);
      return;
    }
    dispatch(loginSlice.actions.resetEsiaFlow(null));
    dispatch(loginSlice.actions.setEsiaRegistrationInfo(null));
    dispatch(loginStepSlice.actions.setLoginStep(LoginState.enter));
    modals.clear();
    setIsFormModalOpen(false);
  }, [dispatch, modals, setIsFormModalOpen]);

  const showFormModal = useCallback(
    (readOnly = false) => {
      setIsFormModalOpen(true);
      hideAndLockGlobalLoader();
      modals.replace({
        render(_modal, _methods) {
          return React.createElement(EsiaRegCardFlowHandler, {
            initialStep: EsiaRegStep.Form,
            onClose: closeFormModal,
            readOnly,
          });
        },
      });
    },
    [hideAndLockGlobalLoader, closeFormModal, modals, setIsFormModalOpen],
  );

  useEffect(() => {
    if (!useDesktopReissueForm) return;
    const unsubscribe = navigation.addListener('blur', closeFormModal);
    return () => {
      unsubscribe();
      closeFormModal();
    };
  }, [useDesktopReissueForm, navigation, closeFormModal]);

  const openForm = useCallback(
    (readOnly = false) => {
      if (useDesktopReissueForm) {
        showFormModal(readOnly);
        return;
      }
      hideAndLockGlobalLoader();
      const navigate = navigation.navigate as (screen: string, params?: object) => void;
      if (isAuthorized) {
        navigate(Screens.MainTabs, {
          screen: Screens.Profile,
          params: {
            screen: Screens.EsiaRegistrationCard,
            params: {readOnly, fromReissue: true},
          },
        });
        return;
      }
      navigate(Screens.EsiaRegistrationCard, {readOnly});
    },
    [hideAndLockGlobalLoader, isAuthorized, navigation, showFormModal, useDesktopReissueForm],
  );

  const {submitting, setSubmitting, onEsiaClick} = useReissueEsia({
    isDesktop,
    isAuthorized,
    esiaRegInfo,
    holderProfile,
    hideAndLockGlobalLoader,
    cancelEsia,
    unlockGlobalLoader,
    showGlobalLoader,
    navigation,
    handledEsiaTokenRef,
  });

  const {onContinue} = useReissueContinue({
    holderProfile,
    hideAndLockGlobalLoader,
    unlockGlobalLoader,
    showGlobalLoader,
    openForm,
    prepareRegCardState,
    setSubmitting,
  });

  useReissueReturnFromEsia({
    isFocused,
    isDesktop,
    isAuthorized,
    esiaRegInfo,
    hideAndLockGlobalLoader,
    openActivation,
    openForm,
    prepareRegCardState,
    handledEsiaTokenRef,
    activationOpenedRef,
    setEsiaLogoutLink,
  });

  return {submitting: submitting || Boolean(esiaLogoutLink), esiaLogoutLink, onEsiaClick, onContinue};
};
